import React, { MouseEvent } from "react";
import ReactDOM from "react-dom";
import * as _ from "lodash"
// @ts-ignore
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea, ResponsiveContainer } from
    "recharts";

import * as SmartDrawings from "../../smartDrawings";

interface StatusChartProps {
    viewer: Autodesk.Viewing.GuiViewer3D
}

interface WeekData {
    week: number,
    value: number
}

interface StatusSerie {
    name: string,
    type: string,
    subtype: string,
    data: WeekData[]
}

interface Reference {
    y1: number,
    y2: number,
    name: string
}

const series: StatusSerie[] = [
    {
        name: "Basic Plan",
        type: "basic",
        subtype: "plan",
        data: [
            { week: 0, value: 0 },
            { week: 4, value: 30 },
            { week: 7, value: 60 },
            { week: 10, value: 90 },
            { week: 13, value: 100 },
        ]
    },
    {
        name: "Basic Estimate",
        type: "basic",
        subtype: "estimate",
        data: [
            { week: 1.5, value: 0 },
            { week: 5.9, value: 30 },
            { week: 9.2, value: 60 },
            { week: 12.5, value: 90 },
            { week: 15.8, value: 100 },
        ]
    },
    {
        name: "Basic Real",
        type: "basic",
        subtype: "real",
        data: [
            { week: 1.5, value: 0 },
            { week: 5.9, value: 30 },
            { week: 9.2, value: 60 },
        ]
    },
    {
        name: "Detail Plan",
        type: "detail",
        subtype: "plan",
        data: [
            { week: 5, value: 0 },
            { week: 8, value: 30 },
            { week: 11, value: 60 },
            { week: 14, value: 90 },
            { week: 15, value: 100 },
        ]
    },
    {
        name: "Detail Estimate",
        type: "detail",
        subtype: "estimate",
        data: [
            { week: 6.5, value: 0 },
            { week: 10.1, value: 30 },
            { week: 13.7, value: 60 },
            { week: 17.3, value: 90 },
            { week: 18.5, value: 100 },
        ]
    },
    {
        name: "Detail Real",
        type: "detail",
        subtype: "real",
        data: [
            { week: 6.5, value: 0 },
            { week: 10.1, value: 30 },
        ]
    },
    {
        name: "Manufacturing Plan",
        type: "manufacturing",
        subtype: "plan",
        data: [
            { week: 11, value: 0 },
            { week: 13, value: 30 },
            { week: 16, value: 60 },
            { week: 19, value: 90 },
            { week: 22, value: 100 },
        ]
    },
    {
        name: "Manufacturing Estimate",
        type: "manufacturing",
        subtype: "estimate",
        data: [
            { week: 12.5, value: 0 },
            { week: 15.1, value: 30 },
            { week: 19, value: 60 },
            { week: 22.9, value: 90 },
            { week: 26.8, value: 100 },
        ]
    },
    {
        name: "Manufacturing Real",
        type: "manufacturing",
        subtype: "real",
        data: [
        ]
    },
    {
        name: "Site Plan",
        type: "site",
        subtype: "plan",
        data: [
            { week: 17, value: 0 },
            { week: 21, value: 30 },
            { week: 25, value: 60 },
            { week: 29, value: 90 },
            { week: 31, value: 100 },
        ]
    },
    {
        name: "Site Estimate",
        type: "site",
        subtype: "estimate",
        data: [
            { week: 18.5, value: 0 },
            { week: 24.5, value: 30 },
            { week: 30.5, value: 60 },
            { week: 36.5, value: 90 },
            { week: 39.5, value: 100 },
        ]
    },
    {
        name: "Site Real",
        type: "site",
        subtype: "real",
        data: [
        ]
    }
];

const referenceAreas: Reference[] = [
    { y1: 0, y2: 30, name: "DIV1" },
    { y1: 30, y2: 60, name: "DIV2" },
    { y1: 60, y2: 90, name: "DIV3" },
    { y1: 90, y2: 100, name: "ESP" }
];

const colors = {
    "basic": "#0000FF",
    "detail": "#00FF00",
    "manufacturing": "#FF0000",
    "site": "#000000",
};

const rightLabel = (text: string) => {
    return { value: text, position: "insideRight" as any };
};

const dash = (series: StatusSerie) => {
    switch (series.subtype) {
    case "estimate":
        return "2 2";
    case "real":
        return "8 2";
    default:
        return null;
    }
};

const CustomTooltip: React.SFC<any> = (e: any) => {
    const { active, payload, label } = e;
    if (active) {
        console.log(e);
        return (
            <div className="custom-tooltip">
                <p className="label">{`${label} : ${payload[0].value}`}</p>
                <p className="desc">Anything you want can be displayed here.</p>
            </div>
        );
    }

    return null;
};

export default class StatusChart extends React.Component<StatusChartProps> {

    constructor(props) {
        super(props);
    }

    componentWillMount(): void {

    }

    componentWillUnmount(): void {

    }

    referenceFill(index: number): string {
        return index % 2 === 0 ? "#FFF" : "#CCC";
    }

    referenceFillOpacity(index: number): number {
        return index % 2 === 0 ? 0 : 0.3;
    }

    render() {
        return (
            <ResponsiveContainer>
                <LineChart>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="week" type="number" domain={[0, "dataMax"]} tickCount={10}/>
                    <YAxis dataKey="value" unit="%" domain={[0, 100]} tickCount={11}/>

                    <Legend iconType="plainline"/>
                    {
                        series.map(s => (
                            <Line dataKey="value" data={s.data} name={s.name} key={s.name} stroke={colors[s.type]
                        } strokeDasharray={dash(s)} strokeWidth={s.subtype === "real" ? 3 : 1}/>
                        ))}

                    {
                        referenceAreas.map((r, index) =>
                            (<ReferenceArea key={r.name} y1={r.y1} y2={r.y2} fill={this.referenceFill(index)} fillOpacity={this.referenceFillOpacity(index)} label={rightLabel(r.name)}/>)
                            )} 

                </LineChart>
            </ResponsiveContainer>
        );
    }
}